/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	type Context,
	type FocusEvent,
	type KeyboardEvent,
	type MouseEvent,
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react';

import { jsx, keyframes } from '@compiled/react';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import ButtonItem from '@atlaskit/menu/button-item';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useThemeObserver } from '@atlaskit/tokens/use-theme-observer';

import type { RemixHiddenOptionKind } from '../../../types';

import { SUB_TYPE_ICON_URLS } from './constants';
import messages from './messages';
import { getSubTypeIconComponent } from './subtype-icon-resolver';
import type { RemixOption, RemixOptionKind, RemixSubType } from './types';
import { getRemixMenuOptions } from './utils';

export type CascadeFlyoutDirection = 'left' | 'right';
export type CascadeVerticalDirection = 'down' | 'up';

export const CascadeVerticalDirectionContext: Context<CascadeVerticalDirection> =
	createContext<CascadeVerticalDirection>('down');

export function getCascadeVerticalDirectionForPlacement(
	placement: string | undefined,
): CascadeVerticalDirection {
	return placement?.startsWith('top') ? 'up' : 'down';
}

// Width of a menu column (top-level categories and sub-type submenu).
const CATEGORY_WIDTH = 280;
const SUBTYPE_WIDTH = 160;
const PREVIEW_WIDTH = 144;
const PREVIEW_HEIGHT = 104;
const CASCADE_COLUMN_GAP = 6;
const CASCADE_VIEWPORT_MARGIN = 8;
const CASCADE_FLYOUT_WIDTH = SUBTYPE_WIDTH + PREVIEW_WIDTH + CASCADE_COLUMN_GAP * 2;

export function getCascadeDirectionForAnchor(
	anchorRect: DOMRect,
	viewportWidth: number,
): CascadeFlyoutDirection {
	const fullCascadeWidth = CATEGORY_WIDTH + CASCADE_FLYOUT_WIDTH;
	const availableRightForFullCascade = viewportWidth - anchorRect.left - CASCADE_VIEWPORT_MARGIN;
	const availableLeftForFlyout = anchorRect.left - CASCADE_VIEWPORT_MARGIN;

	return availableRightForFullCascade < fullCascadeWidth &&
		availableLeftForFlyout >= CASCADE_FLYOUT_WIDTH
		? 'left'
		: 'right';
}

const subtleSlideIn = keyframes({
	from: { opacity: 0, transform: 'translateX(-8px)' },
	to: { opacity: 1, transform: 'translateX(0)' },
});

const menuStyles = cssMap({
	// Keep every layer in normal flow so ADS Popup measures/renders the whole cascade.
	// In left mode, Popup uses bottom-end placement and row-reverse keeps the category
	// column by the trigger while subtype/preview columns appear to its left.
	rootRightDown: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: token('space.075'),
	},
	rootLeftDown: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'flex-start',
		gap: token('space.075'),
	},
	rootRightUp: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'flex-end',
		gap: token('space.075'),
	},
	rootLeftUp: {
		display: 'flex',
		flexDirection: 'row-reverse',
		alignItems: 'flex-end',
		gap: token('space.075'),
	},
	categoryColumn: {
		width: `${CATEGORY_WIDTH}px`,
		flex: '0 0 auto',
		display: 'flex',
		flexDirection: 'column',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	},
	categoryHeader: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.200'),
		paddingInlineStart: token('space.200'),
		paddingInlineEnd: token('space.200'),
	},
	// Keep the measured vertical offset separate from the surface animation so their
	// transforms compose instead of overriding each other.
	subTypeColumnPositioner: {
		width: `${SUBTYPE_WIDTH}px`,
		flex: '0 0 auto',
	},
	subTypeColumn: {
		width: '100%',
		overflow: 'hidden',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		animationName: subtleSlideIn,
		animationDuration: '200ms',
		animationTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		animationFillMode: 'backwards',
		'@media (prefers-reduced-motion: reduce)': {
			animation: 'none',
		},
	},
	previewColumnRight: {
		width: `${PREVIEW_WIDTH}px`,
		height: `${PREVIEW_HEIGHT}px`,
		flex: '0 0 auto',
		alignSelf: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		transitionProperty: 'transform',
		transitionDuration: '150ms',
		transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	previewColumnLeft: {
		width: `${PREVIEW_WIDTH}px`,
		height: `${PREVIEW_HEIGHT}px`,
		flex: '0 0 auto',
		alignSelf: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlockStart: token('space.100'),
		paddingBlockEnd: token('space.100'),
		paddingInlineStart: token('space.100'),
		paddingInlineEnd: token('space.100'),
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		transitionProperty: 'transform',
		transitionDuration: '150ms',
		transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	// The Image artwork has the same aspect ratio as the preview card, so it can fill the card
	// without cropping. This variant is local to the cascading menu and deliberately bypasses the
	// negative-margin compensation that ImageIcon needs inside the padded variant-2 tile.
	previewColumnImageEdgeToEdge: {
		width: `${PREVIEW_WIDTH}px`,
		height: `${PREVIEW_HEIGHT}px`,
		flex: '0 0 auto',
		alignSelf: 'auto',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		paddingBlock: 0,
		paddingInline: 0,
		overflow: 'hidden',
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
		transitionProperty: 'transform',
		transitionDuration: '150ms',
		transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},
	previewImageEdgeToEdge: {
		display: 'block',
		width: '100%',
		height: '100%',
		objectFit: 'cover',
	},
	// Trailing chevron for a category row that opens a submenu.
	categoryTrailing: {
		display: 'flex',
		alignItems: 'center',
	},
	previewContent: {
		width: '100%',
		height: '100%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		pointerEvents: 'none',
	},
});

interface PreviewOffset {
	blockStart: number;
	blockEnd: number;
}

interface RemixSubTypeColumnProps {
	subTypes: RemixSubType[];
	categoryOffset: PreviewOffset;
	pickForMeTestId: string;
	onSubTypeSelect: (subTypeValue: string) => void;
	onPickForMe: () => void;
	onClearPreview: () => void;
	onHoverSubType: (subTypeValue: string, previewOffset: PreviewOffset) => void;
}

function RemixSubTypeColumn({
	subTypes,
	categoryOffset,
	pickForMeTestId,
	onSubTypeSelect,
	onPickForMe,
	onClearPreview,
	onHoverSubType,
}: RemixSubTypeColumnProps) {
	const { formatMessage } = useIntl();
	const verticalDirection = useContext(CascadeVerticalDirectionContext);
	const columnRef = useRef<HTMLDivElement>(null);

	const handleHoverSubType = useCallback(
		(subTypeValue: string, rowElement: HTMLElement) => {
			const columnElement = columnRef.current;
			const rootElement = columnElement?.parentElement;
			if (!columnElement || !rootElement) {
				onHoverSubType(subTypeValue, { blockStart: 0, blockEnd: 0 });
				return;
			}

			const rootRect = rootElement.getBoundingClientRect();
			const columnRect = columnElement.getBoundingClientRect();
			const rowRect = rowElement.getBoundingClientRect();
			const isFirstSubType = subTypes[0]?.value === subTypeValue;

			if (isFirstSubType) {
				// Reuse the subtype column's positioning source instead of measuring its translated top.
				// This prevents small rounding/timing differences between the first preview and the card.
				// Downward roots align from the top. Upward roots align from the bottom, so account for
				// the difference between the subtype and preview heights. Left/right does not affect it.
				onHoverSubType(subTypeValue, {
					blockStart: categoryOffset.blockStart,
					blockEnd: columnRect.height + categoryOffset.blockEnd - PREVIEW_HEIGHT,
				});
				return;
			}

			// Every non-first preview preserves the original exact row-centering calculation.
			const previewTop = rowRect.top + rowRect.height / 2 - PREVIEW_HEIGHT / 2;
			onHoverSubType(subTypeValue, {
				blockStart: previewTop - rootRect.top,
				blockEnd: rootRect.bottom - previewTop - PREVIEW_HEIGHT,
			});
		},
		[categoryOffset, onHoverSubType, subTypes],
	);

	return (
		<Box
			ref={columnRef}
			xcss={menuStyles.subTypeColumnPositioner}
			style={{
				transform: `translateY(${
					verticalDirection === 'up' ? -categoryOffset.blockEnd : categoryOffset.blockStart
				}px)`,
			}}
			testId="remix-menu-submenu"
		>
			<Box xcss={menuStyles.subTypeColumn} testId="remix-menu-submenu-surface">
				<MenuGroup>
					<Section>
						{subTypes.map((subType) => {
							const label = formatMessage(subType.labelMessage);
							return (
								<Box
									key={subType.value}
									testId={`remix-menu-subtype-row-${subType.value}`}
									onMouseEnter={(event: MouseEvent<HTMLDivElement>) =>
										handleHoverSubType(subType.value, event.currentTarget)
									}
									onFocus={(event: FocusEvent<HTMLDivElement>) =>
										handleHoverSubType(subType.value, event.currentTarget)
									}
								>
									<ButtonItem
										testId={subType.testId ?? `remix-menu-subtype-${subType.value}`}
										onClick={() => onSubTypeSelect(subType.value)}
									>
										{label}
									</ButtonItem>
								</Box>
							);
						})}
					</Section>
					<Section hasSeparator>
						<Box onMouseEnter={onClearPreview} onFocus={onClearPreview}>
							<ButtonItem testId={pickForMeTestId} onClick={onPickForMe}>
								{formatMessage(messages.pickForMe)}
							</ButtonItem>
						</Box>
					</Section>
				</MenuGroup>
			</Box>
		</Box>
	);
}

function RemixPreviewColumn({
	subType,
	direction,
	previewOffset,
	isAnimatedArtworkEnabled,
}: {
	subType: RemixSubType | null;
	direction: CascadeFlyoutDirection;
	previewOffset: PreviewOffset;
	isAnimatedArtworkEnabled: boolean;
}) {
	const { formatMessage } = useIntl();
	const { colorMode } = useThemeObserver();
	const verticalDirection = useContext(CascadeVerticalDirectionContext);

	if (!subType) {
		return null;
	}

	const label = formatMessage(subType.labelMessage);
	const isImagePreview = subType.value === 'image';
	const IconComponent = isImagePreview
		? null
		: getSubTypeIconComponent(subType.value, isAnimatedArtworkEnabled);
	const previewArtwork = isImagePreview ? (
		<Box
			as="img"
			src={colorMode === 'dark' ? SUB_TYPE_ICON_URLS.image.dark : SUB_TYPE_ICON_URLS.image.light}
			alt={label}
			aria-hidden="true"
			xcss={menuStyles.previewImageEdgeToEdge}
			testId="remix-menu-preview"
		/>
	) : IconComponent ? (
		<Box xcss={menuStyles.previewContent} testId="remix-menu-preview">
			<IconComponent alt={label} />
		</Box>
	) : null;

	if (!previewArtwork) {
		return null;
	}

	return (
		<Box
			xcss={
				isImagePreview
					? menuStyles.previewColumnImageEdgeToEdge
					: direction === 'left'
						? menuStyles.previewColumnLeft
						: menuStyles.previewColumnRight
			}
			style={{
				transform: `translateY(${
					verticalDirection === 'up' ? -previewOffset.blockEnd : previewOffset.blockStart
				}px)`,
			}}
			testId="remix-menu-preview-column"
		>
			{previewArtwork}
		</Box>
	);
}

interface RemixCategoryItemProps {
	option: RemixOption;
	onActivate: (option: RemixOption, rowElement: HTMLElement) => void;
	onFlatSelect: (type: RemixOptionKind) => void;
}

function RemixCategoryItem({ option, onActivate, onFlatSelect }: RemixCategoryItemProps) {
	const { formatMessage } = useIntl();

	const label = formatMessage(option.labelMessage);
	const optionIconAlt = formatMessage(option.iconAltMessage);
	const OptionIcon = option.icon;
	const hasSubTypes = option.subTypes.length > 0;

	if (!hasSubTypes) {
		return (
			<Box
				testId={`remix-menu-category-row-${option.type}`}
				// `mousemove`, not `mouseenter`: the browser re-fires boundary events (`mouseover` /
				// `mouseenter`) whenever layout shifts under a stationary pointer. Focusing the
				// describe-prompt footer resizes the category column and repositions the popup, which
				// would otherwise "hover" whichever row slid under the cursor and open its flyout.
				// `mousemove` only fires for genuine pointer movement. `onActivate` is idempotent per
				// row, so the repeated calls while moving across a row collapse into one activation.
				onMouseMove={(event: MouseEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
				onFocus={(event: FocusEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
			>
				<ButtonItem
					iconBefore={<OptionIcon label={optionIconAlt} size="small" color={token('color.icon')} />}
					testId={option.testId}
					onClick={() => onFlatSelect(option.type)}
				>
					{label}
				</ButtonItem>
			</Box>
		);
	}

	return (
		<Box
			testId={`remix-menu-category-row-${option.type}`}
			// See the note on the flat-option branch above: `mousemove` avoids spurious activation
			// when layout shifts under a stationary pointer.
			onMouseMove={(event: MouseEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
			onFocus={(event: FocusEvent<HTMLDivElement>) => onActivate(option, event.currentTarget)}
		>
			<ButtonItem
				iconBefore={<OptionIcon label={optionIconAlt} size="small" color={token('color.icon')} />}
				iconAfter={
					<Box xcss={menuStyles.categoryTrailing}>
						<ChevronRightIcon label="" color={token('color.icon')} size="small" />
					</Box>
				}
				testId={option.testId}
				onClick={(event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) =>
					onActivate(option, event.currentTarget)
				}
			>
				{label}
			</ButtonItem>
		</Box>
	);
}

export interface RemixCascadingMenuContentProps {
	onOptionSelect?: (parameters: {
		type: RemixOptionKind;
		subType?: string;
		userPrompt?: string;
	}) => void;
	options?: RemixOption[];
	hiddenOptions?: readonly RemixHiddenOptionKind[];
	flyoutDirection?: CascadeFlyoutDirection;
	showFlyout?: boolean;
	/** Rendered above the category list within the first menu layer. */
	header?: ReactNode;
	/** Rendered below the category list, e.g. the free-text prompt row. */
	footer?: ReactNode;
}

/**
 * RemixCascadingMenuContent — lightweight cascading dropdown menu (like a toolbar /
 * right-click menu): top-level categories (Charts, Diagrams, …) each open a right-side
 * submenu of sub-types (Line, Area, …). Selecting a sub-type emits `{ type, subType }`.
 * "Pick for me" randomly chooses a sub-type and emits through that same selection path.
 * Categories without sub-types are directly clickable and emit only `{ type }`, letting
 * the backend choose.
 *
 * This is the "inline dropdown" Remix menu variant, used by RemixPopup (anchored popup
 * presentation) as a lighter-weight alternative to RemixOverlayContent's accordion.
 */
export function RemixCascadingMenuContent({
	onOptionSelect,
	options,
	hiddenOptions,
	flyoutDirection = 'right',
	showFlyout = true,
	header,
	footer,
}: RemixCascadingMenuContentProps): JSX.Element {
	const verticalDirection = useContext(CascadeVerticalDirectionContext);
	const isAnimatedArtworkEnabled = isExperimentEnabled('cc_maui_polish_changes_batch_6');
	const categoryColumnRef = useRef<HTMLDivElement>(null);
	const resolvedOptions = useMemo(
		() =>
			getRemixMenuOptions({
				source:
					options === undefined
						? { kind: 'preset', preset: 'selection' }
						: {
								kind: 'custom',
								choices: options,
							},
				excludedRemixTypes: hiddenOptions ? [...hiddenOptions] : undefined,
			}),
		[hiddenOptions, options],
	);

	// The category whose sub-types are currently shown in the second column. Only
	// categories that have sub-types open a flyout; the rest close it.
	const [activeType, setActiveType] = useState<RemixOptionKind | null>(null);
	// Measured from both edges of the active category row, positioning the subtype card.
	const [categoryOffset, setCategoryOffset] = useState<PreviewOffset>({
		blockStart: 0,
		blockEnd: 0,
	});
	// The sub-type currently hovered/focused, driving the preview column.
	const [hoveredSubType, setHoveredSubType] = useState<string | null>(null);
	// Measured from both root edges so the preview center follows the subtype row center,
	// including when edge-row previews extend beyond the subtype card.
	const [previewOffset, setPreviewOffset] = useState<PreviewOffset>({
		blockStart: 0,
		blockEnd: 0,
	});

	const activeOption = useMemo(
		() => resolvedOptions.find((opt) => opt.type === activeType && opt.subTypes.length > 0) ?? null,
		[resolvedOptions, activeType],
	);
	const activeSubTypes = activeOption?.subTypes ?? [];
	const hoveredSubTypeDef = activeSubTypes.find((st) => st.value === hoveredSubType) ?? null;
	const firstCategoryType = resolvedOptions[0]?.type;
	// The category row that most recently claimed the flyout. Kept in a ref rather than derived from
	// `activeType` because categories without sub-types also "claim" the flyout (by closing it), and
	// because it must stay stable across the repeated `mousemove` events fired while the pointer
	// travels across a single row.
	const activatedTypeRef = useRef<RemixOptionKind | null>(null);

	// Resets the sub-type preview. Deliberately bails out when there is nothing to clear, so it is
	// safe to call from the high-frequency `mousemove` path without forcing a re-render.
	const clearPreview = useCallback(() => {
		setHoveredSubType(null);
		setPreviewOffset((current) =>
			current.blockStart === 0 && current.blockEnd === 0 ? current : { blockStart: 0, blockEnd: 0 },
		);
	}, []);

	const handleActivate = useCallback(
		(option: RemixOption, rowElement: HTMLElement) => {
			if (activatedTypeRef.current === option.type) {
				// This row already owns the flyout — usually because the pointer is travelling back out
				// of the flyout across its own category row. The flyout and its offset are unchanged
				// (the row has not moved relative to the column), but any sub-type preview picked up on
				// the way out is now stale, so clear it exactly as a fresh activation would.
				clearPreview();
				return;
			}
			activatedTypeRef.current = option.type;

			const categoryRect = categoryColumnRef.current?.getBoundingClientRect();
			const rowRect = rowElement.getBoundingClientRect();
			const isFirstCategory = option.type === firstCategoryType;
			setCategoryOffset(
				categoryRect
					? {
							// ADS Section adds a 6px leading spacer before its first row. The first category's
							// flyout belongs at the category card edge, not below that internal content inset.
							blockStart: isFirstCategory ? 0 : Math.max(0, rowRect.top - categoryRect.top),
							blockEnd: Math.max(0, categoryRect.bottom - rowRect.bottom),
						}
					: { blockStart: 0, blockEnd: 0 },
			);
			setActiveType(option.subTypes.length > 0 ? option.type : null);
			clearPreview();
		},
		[clearPreview, firstCategoryType],
	);

	const handleHoverSubType = useCallback(
		(subTypeValue: string, nextPreviewOffset: PreviewOffset) => {
			setHoveredSubType(subTypeValue);
			setPreviewOffset(nextPreviewOffset);
		},
		[],
	);

	// Collapses the sub-type flyout and its preview back to the bare category column. Used when the
	// pointer or keyboard focus moves into the describe-prompt footer: the flyout belongs to a
	// category row, so leaving the category list for the free-text prompt must not leave a stale
	// submenu hanging beside the menu.
	const handleDismissFlyout = useCallback(() => {
		if (activatedTypeRef.current === null) {
			return;
		}
		activatedTypeRef.current = null;
		setActiveType(null);
		clearPreview();
	}, [clearPreview]);

	const handleFlatSelect = useCallback(
		(type: RemixOptionKind) => {
			onOptionSelect?.({ type });
		},
		[onOptionSelect],
	);

	const handleSubTypeSelect = useCallback(
		(subTypeValue: string) => {
			if (!activeOption) {
				return;
			}
			if (fg('cc-maui-phase-3')) {
				const subTypeDef = activeOption.subTypes.find((st) => st.value === subTypeValue);
				onOptionSelect?.({
					type: subTypeDef?.emitType ?? activeOption.type,
					subType: subTypeValue,
				});
				return;
			}
			onOptionSelect?.({ type: activeOption.type, subType: subTypeValue });
		},
		[activeOption, onOptionSelect],
	);

	const handlePickForMe = useCallback(() => {
		if (!activeOption) {
			return;
		}

		const randomSubType =
			activeOption.subTypes[Math.floor(Math.random() * activeOption.subTypes.length)];
		if (!randomSubType) {
			return;
		}

		handleSubTypeSelect(randomSubType.value);
	}, [activeOption, handleSubTypeSelect]);

	return (
		<Box
			xcss={
				flyoutDirection === 'left'
					? verticalDirection === 'up'
						? menuStyles.rootLeftUp
						: menuStyles.rootLeftDown
					: verticalDirection === 'up'
						? menuStyles.rootRightUp
						: menuStyles.rootRightDown
			}
			testId="remix-menu"
		>
			<Box ref={categoryColumnRef} xcss={menuStyles.categoryColumn} testId="remix-menu-categories">
				{header ? (
					<Box xcss={menuStyles.categoryHeader} testId="remix-menu-header">
						{header}
					</Box>
				) : null}
				<Box>
					<MenuGroup>
						<Section>
							{resolvedOptions.map((option) => (
								<RemixCategoryItem
									key={option.type}
									option={option}
									onActivate={handleActivate}
									onFlatSelect={handleFlatSelect}
								/>
							))}
						</Section>
					</MenuGroup>
				</Box>
				{footer ? (
					<Box
						// `onFocus` maps to the bubbling `focusin`, so this also catches focus landing on the
						// textarea or submit button nested inside the footer.
						onFocus={handleDismissFlyout}
						onMouseEnter={handleDismissFlyout}
						testId="remix-menu-footer-region"
					>
						{footer}
					</Box>
				) : null}
			</Box>
			{showFlyout && activeOption ? (
				<RemixSubTypeColumn
					key={activeType}
					subTypes={activeSubTypes}
					categoryOffset={categoryOffset}
					pickForMeTestId={`${activeOption.testId}-pick-for-me`}
					onSubTypeSelect={handleSubTypeSelect}
					onPickForMe={handlePickForMe}
					onClearPreview={clearPreview}
					onHoverSubType={handleHoverSubType}
				/>
			) : null}
			{showFlyout && hoveredSubTypeDef ? (
				<RemixPreviewColumn
					subType={hoveredSubTypeDef}
					direction={flyoutDirection}
					previewOffset={previewOffset}
					isAnimatedArtworkEnabled={isAnimatedArtworkEnabled}
				/>
			) : null}
		</Box>
	);
}
